import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { I18nextProvider } from "react-i18next";
import { stringify } from "qs";
import { camelize } from "humps";
import { store } from "main/javascripts/store";
import { HeaderContainer } from "main/javascripts/containers/organisms/Header/HeaderContainer";
import { FooterContainer } from "main/javascripts/containers/organisms/Footer/FooterContainer";
import {
  defaultStyles,
  extended,
  GLOBALS,
} from "main/javascripts/styles/globalStyle";
import i18n from "i18n";
import MainHead from "main/javascripts/components/organisms/MainHead/MainHead";
import { useConstructor } from "main/javascripts/hooks/common/useConstructor";
import { MainBlock } from "main/javascripts/components/atoms/Block/MainBlock";
import {
  fetchPostsShowPage,
  putPostAccess,
  initPost,
} from "main/javascripts/features/post/postSlice";
import { PostShowMain } from "main/javascripts/features/post/components/PostShowMain";
import { changeSearchCategory } from "main/javascripts/features/search/searchPageSlice";
import { getPostHtml } from "main/javascripts/utils/serverSide/PostUtil";
import { editorConfig } from "main/javascripts/features/post/PostNodes";

interface IProps {
  post: any;
  html: any;
  locale: string;
}

function ArticleShow(props: IProps) {
  const { post, html, locale } = props;
  const { title, description } = post.metaTags;

  useConstructor(() => {
    store.dispatch(initPost(post));
    if (post.searchTypes?.length > 0) {
      store.dispatch(changeSearchCategory(camelize(post.searchTypes[0])));
    }
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    extended.css(defaultStyles[GLOBALS]);
  });

  useEffect(() => {
    store.dispatch(putPostAccess({ id: post.id }));
  }, []);

  const origin = process.env.BASE_URL;

  return (
    <Provider store={store}>
      <MainHead origin={origin} title={title} description={description} />
      <I18nextProvider i18n={i18n}>
        <HeaderContainer />
        <MainBlock>
          <PostShowMain html={html} />
        </MainBlock>
        <FooterContainer />
      </I18nextProvider>
    </Provider>
  );
}

export async function getStaticProps(ctx) {
  const locale = ctx.locale;
  const param = ctx.params;
  const postId = param.postId;
  const paramString: string = stringify(param, { arrayFormat: "repeat" });
  const { payload } = await store.dispatch<any>(
    fetchPostsShowPage({
      id: postId,
      param: paramString,
      isServerSide: true,
    })
  );

  const json = payload?.post?.content?.editorStateJson;
  let html = "";
  if (json) {
    html = await getPostHtml(json, editorConfig);
  }

  return {
    props: {
      locale,
      post: payload?.post,
      html,
    },
    revalidate: 60,
  };
}

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export default ArticleShow;
